import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../services/api";

export const WishlistContext = createContext();

const isLoggedIn = () =>
  localStorage.getItem("authToken") && localStorage.getItem("isGuest") !== "true";

export const WishlistProvider = ({ children }) => {
  const [enabled, setEnabled] = useState(false);     // module on this store's plan
  const [ids, setIds] = useState(new Set());         // wishlisted product ids (for hearts)
  const [items, setItems] = useState([]);            // full products (for the wishlist page)
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(() => {
    api.get("/wishlist/status").then((r) => setEnabled(!!r.data.enabled)).catch(() => setEnabled(false));
    if (isLoggedIn()) {
      api.get("/wishlist/ids").then((r) => setIds(new Set((r.data.ids || []).map(String)))).catch(() => {});
    } else {
      setIds(new Set());
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Full product list for the /wishlist page.
  const fetchItems = useCallback(async () => {
    if (!isLoggedIn()) { setItems([]); return; }
    setLoading(true);
    try {
      const res = await api.get("/wishlist");
      setItems(res.data.items || []);
      setIds(new Set((res.data.items || []).map((p) => String(p._id))));
    } catch {
      /* leave as-is */
    } finally {
      setLoading(false);
    }
  }, []);

  const isWishlisted = (id) => ids.has(String(id));

  const toggle = async (id) => {
    id = String(id);
    if (!isLoggedIn()) { toast.info("Please log in to save items to your wishlist"); return; }
    if (ids.has(id)) {
      setIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
      setItems((prev) => prev.filter((p) => String(p._id) !== id));
      try { await api.delete(`/wishlist/${id}`); } catch { refresh(); toast.error("Could not update wishlist"); }
    } else {
      setIds((prev) => new Set(prev).add(id));
      try { await api.post("/wishlist", { product_id: id }); toast.success("Saved to wishlist ❤️"); }
      catch { refresh(); toast.error("Could not update wishlist"); }
    }
  };

  return (
    <WishlistContext.Provider
      value={{ enabled, ids, items, loading, isWishlisted, toggle, refresh, fetchItems, count: ids.size }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error("useWishlist must be used within a WishlistProvider");
  return ctx;
};
